/*
 * Toy retrieval index for the Findy instrument (spec f.6).
 * Eight invented documents, twelve hand-assigned concept dimensions, a small synonym table.
 * Nothing here is a real document, a real person or an amount tied to anyone.
 * The corpus deliberately contains no passage about a wifi password, so the trap chip
 * refuses structurally rather than through a special case.
 */

export interface FindyDoc {
  file: string;
  /* Page count of the whole document. */
  pages: number;
  /* Twelve concept weights, see CONCEPTS for the axis names. */
  concept: number[];
  /* Words the document is indexed under (stemmed at index build). */
  tokens: string[];
  /* The cited passage, with `mark` the substring the receipt highlights. */
  passage: string;
  mark: string;
  /* The cited page. */
  page: number;
  /* The one-line answer Findy gives when this passage wins. */
  answer: string;
  /* Only these reviewed questions are supported by this sample passage. */
  supportedQuestions: string[];
}

/* Reciprocal-rank fusion constant, the same k production uses. */
export const RRF_K = 60;

/* The twelve concept axes, in vector order. */
export const CONCEPTS = [
  'insurance',
  'dates',
  'money',
  'housing',
  'vehicle',
  'health',
  'repair',
  'warranty',
  'education',
  'contract',
  'official',
  'family',
] as const;

export const FINDY_DOCS: FindyDoc[] = [
  {
    file: 'policy-2026.pdf',
    supportedQuestions: [
      'When does my insurance expire?',
      'When does my policy expire?',
      'What is my insurance expiration date?',
    ],
    pages: 14,
    concept: [1.0, 0.8, 0.4, 0.3, 0, 0.1, 0, 0.1, 0, 0.6, 0.2, 0.1],
    tokens: [
      'policy',
      'insurance',
      'insured',
      'coverage',
      'cover',
      'valid',
      'through',
      'march',
      '12',
      '2027',
      'expires',
      'expiration',
      'renewal',
      'premium',
      'deductible',
      'home',
      'contents',
      'liability',
      'effective',
      'term',
    ],
    passage: 'coverage valid through March 12, 2027, subject to timely premium payment.',
    mark: 'valid through March 12, 2027',
    page: 3,
    answer: 'It expires on March 12, 2027.',
  },
  {
    file: 'scan0032.pdf',
    supportedQuestions: [
      'The lease that raises rent with inflation',
      'Which lease raises rent with inflation?',
    ],
    pages: 6,
    concept: [0.1, 0.4, 0.6, 1.0, 0, 0, 0, 0, 0, 0.9, 0.1, 0],
    tokens: [
      'lease',
      'agreement',
      'apt',
      '302',
      'apartment',
      'rent',
      'annual',
      'inflation',
      'indexed',
      'adjustment',
      'cpi',
      'landlord',
      'tenant',
      'raise',
      'monthly',
      'term',
      'deposit',
      'signed',
    ],
    passage: 'annual inflation-indexed rent adjustment applied each July 1, capped at the CPI.',
    mark: 'inflation-indexed rent adjustment',
    page: 1,
    answer: 'Lease agreement, Apt 302.',
  },
  {
    file: 'hvac-invoice.pdf',
    supportedQuestions: ['How much was the AC repair?', 'How much did the HVAC repair cost?'],
    pages: 2,
    concept: [0, 0.3, 0.9, 0.3, 0, 0, 1.0, 0.3, 0, 0.1, 0, 0],
    tokens: [
      'hvac',
      'invoice',
      'ac',
      'air',
      'conditioning',
      'conditioner',
      'repair',
      'service',
      'total',
      'due',
      '480',
      'paid',
      '06',
      '03',
      'june',
      'technician',
      'compressor',
      'refrigerant',
      'labor',
      'parts',
      'unit',
      'cost',
    ],
    passage: 'total due $480.00, paid 06/03 by card, refrigerant recharge and capacitor.',
    mark: '$480.00, paid 06/03',
    page: 1,
    answer: '$480, paid on June 3.',
  },
  {
    file: 'lease-renewal-2025.pdf',
    supportedQuestions: ['When does my lease renewal end?'],
    pages: 4,
    concept: [0.1, 0.8, 0.5, 1.0, 0, 0, 0, 0, 0, 0.9, 0, 0],
    tokens: [
      'lease',
      'renewal',
      'renew',
      'term',
      'ending',
      'ends',
      'august',
      '31',
      '2026',
      'monthly',
      'rate',
      'rent',
      'same',
      'apartment',
      'landlord',
      'tenant',
      'signed',
      'notice',
      'move',
    ],
    passage: 'renewal term ending August 31, 2026, at the same monthly rate as the prior term.',
    mark: 'ending August 31, 2026',
    page: 2,
    answer: 'The renewal runs through August 31, 2026.',
  },
  {
    file: 'car-registration.pdf',
    supportedQuestions: ['When does my car registration expire?'],
    pages: 1,
    concept: [0.2, 0.7, 0.3, 0, 1.0, 0, 0, 0, 0, 0.1, 0.9, 0],
    tokens: [
      'car',
      'vehicle',
      'registration',
      'registered',
      'plate',
      'valid',
      'until',
      '11',
      '30',
      'november',
      'renewal',
      'notice',
      'dmv',
      'title',
      'owner',
      'fee',
      'sedan',
      'vin',
      'sticker',
    ],
    passage: 'registration valid until 11/30, plate renewal notice enclosed with the fee schedule.',
    mark: 'valid until 11/30',
    page: 1,
    answer: 'Registration is valid until November 30.',
  },
  {
    file: 'dental-claim-0417.pdf',
    supportedQuestions: ['How much did insurance pay for the dental cleaning?'],
    pages: 3,
    concept: [0.7, 0.3, 0.7, 0, 0, 1.0, 0, 0, 0, 0.1, 0.1, 0.2],
    tokens: [
      'dental',
      'dentist',
      'claim',
      '0417',
      'cleaning',
      'insurer',
      'insurance',
      'paid',
      '212',
      'patient',
      'responsibility',
      '38',
      'tooth',
      'teeth',
      'exam',
      'xray',
      'coverage',
      'plan',
      'visit',
    ],
    passage: 'insurer paid $212.00 toward the routine cleaning, patient responsibility $38.00.',
    mark: 'paid $212.00',
    page: 2,
    answer: 'The plan paid $212 of the cleaning.',
  },
  {
    file: 'warranty-fridge.pdf',
    supportedQuestions: ['When does my fridge compressor warranty expire?'],
    pages: 8,
    concept: [0.2, 0.6, 0.2, 0.2, 0, 0, 0.5, 1.0, 0, 0.5, 0, 0],
    tokens: [
      'warranty',
      'limited',
      'fridge',
      'refrigerator',
      'appliance',
      'compressor',
      'covers',
      'covered',
      'through',
      'may',
      '2028',
      'repair',
      'replacement',
      'parts',
      'labor',
      'serial',
      'model',
      'kitchen',
      'purchase',
    ],
    passage: 'limited warranty covers the compressor through May 2028, parts and labor included.',
    mark: 'through May 2028',
    page: 4,
    answer: 'The compressor is covered until May 2028.',
  },
  {
    file: 'school-enrollment.pdf',
    supportedQuestions: ['Is enrollment confirmed for the fall term?'],
    pages: 2,
    concept: [0, 0.6, 0.3, 0, 0, 0.1, 0, 0, 1.0, 0.3, 0.3, 0.8],
    tokens: [
      'school',
      'enrollment',
      'enrolled',
      'confirmed',
      'fall',
      'term',
      'first',
      'day',
      'september',
      '8',
      'student',
      'grade',
      'class',
      'tuition',
      'kid',
      'child',
      'supplies',
      'calendar',
      'form',
    ],
    passage:
      'enrollment for the fall term confirmed, first day September 8, supplies list attached.',
    mark: 'fall term confirmed',
    page: 1,
    answer: 'Enrollment is confirmed for the fall term.',
  },
];

/* The four suggestion chips. The fourth is the trap: no document mentions it. */
export const FINDY_QUESTIONS = [
  'when does my insurance expire?',
  'the lease that raises rent with inflation',
  'how much was the AC repair?',
  'what is my wifi password?',
] as const;

/* The attract cycle types these three, once. */
export const ATTRACT_QUESTIONS = FINDY_QUESTIONS.slice(0, 3);

export const DEFAULT_PLACEHOLDER = 'Ask your documents anything.';

/* Words that carry no signal in either leg. Removed before stemming. */
export const STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'my',
  'me',
  'i',
  'of',
  'in',
  'on',
  'at',
  'to',
  'for',
  'with',
  'and',
  'or',
  'what',
  'where',
  'which',
  'who',
  'whom',
  'how',
  'does',
  'do',
  'did',
  'that',
  'this',
  'these',
  'those',
  'it',
  'its',
  'from',
  'as',
  'about',
  'any',
  'has',
  'have',
  'had',
  'will',
  'would',
  'can',
  'could',
  'you',
  'your',
  'our',
  'we',
  'they',
  'their',
  'there',
  'than',
  'then',
  'so',
  'if',
  'not',
  'no',
  'here',
  'up',
  'out',
]);

/* Query term (stemmed) to concept weights: [axis index, weight]. About sixty entries. */
export const SYNONYMS: Record<string, [number, number][]> = {
  insurance: [
    [0, 1],
    [9, 0.3],
  ],
  insure: [[0, 1]],
  insurer: [[0, 0.9]],
  policy: [
    [0, 0.9],
    [9, 0.5],
  ],
  coverage: [[0, 0.9]],
  cover: [
    [0, 0.9],
    [7, 0.3],
  ],
  premium: [
    [0, 0.7],
    [2, 0.5],
  ],
  claim: [
    [0, 0.8],
    [5, 0.3],
  ],
  deductible: [
    [0, 0.7],
    [2, 0.4],
  ],
  expire: [[1, 1]],
  expiration: [[1, 1]],
  expiry: [[1, 1]],
  renew: [
    [1, 0.8],
    [9, 0.4],
  ],
  renewal: [
    [1, 0.8],
    [9, 0.4],
  ],
  valid: [[1, 0.7]],
  deadline: [[1, 1]],
  date: [[1, 1]],
  due: [
    [1, 0.7],
    [2, 0.4],
  ],
  until: [[1, 0.7]],
  when: [[1, 0.6]],
  end: [[1, 0.7]],
  pay: [[2, 1]],
  paid: [[2, 1]],
  payment: [[2, 1]],
  cost: [[2, 1]],
  price: [[2, 1]],
  much: [[2, 0.7]],
  amount: [[2, 1]],
  total: [[2, 0.8]],
  invoice: [
    [2, 0.7],
    [6, 0.4],
  ],
  bill: [[2, 0.8]],
  fee: [[2, 0.8]],
  charge: [[2, 0.8]],
  dollar: [[2, 1]],
  receipt: [[2, 0.6]],
  lease: [
    [3, 1],
    [9, 0.6],
  ],
  rent: [
    [3, 1],
    [2, 0.4],
  ],
  landlord: [
    [3, 0.9],
    [9, 0.3],
  ],
  apartment: [[3, 1]],
  apt: [[3, 1]],
  tenant: [[3, 0.9]],
  inflation: [
    [2, 0.6],
    [3, 0.4],
    [9, 0.3],
  ],
  raise: [
    [2, 0.5],
    [3, 0.3],
  ],
  increase: [
    [2, 0.5],
    [3, 0.3],
  ],
  home: [[3, 0.8]],
  house: [[3, 0.8]],
  car: [[4, 1]],
  vehicle: [[4, 1]],
  registration: [
    [4, 0.6],
    [10, 0.8],
  ],
  plate: [
    [4, 0.8],
    [10, 0.5],
  ],
  dmv: [
    [4, 0.5],
    [10, 1],
  ],
  drive: [[4, 0.8]],
  driver: [[4, 0.8]],
  auto: [[4, 0.8]],
  dental: [
    [5, 1],
    [0, 0.3],
  ],
  dentist: [[5, 1]],
  tooth: [[5, 0.9]],
  teeth: [[5, 0.9]],
  doctor: [[5, 1]],
  medical: [[5, 1]],
  health: [[5, 0.9]],
  clean: [[5, 0.5]],
  repair: [[6, 1]],
  fix: [[6, 1]],
  service: [[6, 0.8]],
  ac: [
    [6, 0.8],
    [7, 0.3],
  ],
  hvac: [[6, 1]],
  air: [[6, 0.5]],
  condition: [[6, 0.8]],
  conditioner: [[6, 0.8]],
  technician: [[6, 0.8]],
  compressor: [
    [6, 0.6],
    [7, 0.5],
  ],
  maintenance: [[6, 0.9]],
  warranty: [
    [7, 1],
    [9, 0.4],
  ],
  guarantee: [[7, 0.9]],
  fridge: [
    [7, 0.8],
    [6, 0.3],
  ],
  refrigerator: [
    [7, 0.8],
    [6, 0.3],
  ],
  appliance: [[7, 0.9]],
  kitchen: [
    [7, 0.5],
    [3, 0.4],
  ],
  school: [
    [8, 1],
    [11, 0.4],
  ],
  enrollment: [[8, 1]],
  enroll: [[8, 1]],
  tuition: [
    [8, 0.8],
    [2, 0.6],
  ],
  class: [[8, 0.7]],
  term: [
    [8, 0.5],
    [1, 0.4],
    [9, 0.4],
  ],
  semester: [[8, 0.9]],
  student: [
    [8, 0.9],
    [11, 0.4],
  ],
  contract: [[9, 1]],
  agreement: [[9, 1]],
  clause: [[9, 0.9]],
  sign: [[9, 0.7]],
  license: [
    [10, 0.9],
    [4, 0.4],
  ],
  government: [[10, 1]],
  state: [[10, 0.6]],
  form: [[10, 0.6]],
  kid: [
    [11, 1],
    [8, 0.4],
  ],
  child: [
    [11, 1],
    [8, 0.4],
  ],
  children: [
    [11, 1],
    [8, 0.4],
  ],
  son: [[11, 1]],
  daughter: [[11, 1]],
  family: [[11, 1]],
};
